import { Button } from 'react-aria-components'

import { css, cva } from '@portal/styled-system/css'

import { env } from '@/shared/config'
import { type Language, useI18n } from '@/shared/lib/i18n'
import { Modal, type ModalProps } from '@/shared/ui'

import { languageNameFonts } from './language-name-fonts'

export type LangDialogProps = ModalProps & {
    onLanguageClick: (lang: Language) => void
}

export function LangDialog({ onLanguageClick, onClose, ...modalProps }: LangDialogProps) {
    const { i18n, lang: selectedLang } = useI18n()

    return (
        <Modal {...modalProps} styles={css.raw({ width: '[23.75rem]' })} onOutsideClick={onClose}>
            <Modal.CloseButton onClose={onClose} />
            <Modal.Header styles={css.raw({ backgroundColor: 'primary' })} icon="fa-solid fa-language">
                {i18n.PORTAL_LANGUAGE}
            </Modal.Header>
            <Modal.Buttons
                styles={css.raw({ flexWrap: 'wrap', rowGap: '2', columnGap: '3', paddingBottom: '5', paddingTop: '4' })}
            >
                {env.LANG_LIST.map((lang) => (
                    <Button
                        onPress={() => onLanguageClick(lang)}
                        key={lang}
                        isDisabled={lang === selectedLang}
                        className={langButtonRecipe({ selected: lang === selectedLang })}
                    >
                        <span className={languageNameFonts[lang]?.className}>{i18n[`lang_${lang}`]}</span>
                    </Button>
                ))}
            </Modal.Buttons>
        </Modal>
    )
}

const langButtonRecipe = cva({
    base: {
        width: '[calc(50% - 0.5rem)]',
        paddingTop: '1',
        paddingBottom: '0.5',
        paddingX: '2',
        outline: 'none', // reset
        backgroundColor: 'transparent', // reset
        border: 'default',
        borderColor: 'transparent',
        borderRadius: 'medium',
        lineHeight: 'normal',
        textWrap: 'nowrap',
        textAlign: 'start',
    },
    variants: {
        selected: {
            true: { color: 'gray-v2.400', cursor: 'auto' },
            false: {
                color: 'primary',
                _hover: { borderColor: 'primary' },
                _active: { color: 'primary.800', borderColor: 'primary.800' },
                _focusVisible: { color: 'primary.800', boxShadow: '[0 0 0 0.25rem token(colors.primary/50)]' },
            },
        },
    },
})
